import { useState, useEffect, useCallback } from 'react'
import MapComponent from '../components/MapComponent'
import { useAuth } from '../context/AuthContext'

interface Walk {
  id: string
  name: string
  description?: string
  published: boolean
  total_days: number
  total_distance_km: number
  total_elevation_gain_m: number
  expected_km?: number
  start_date?: string
  end_date?: string
  created_date: string
}

const HomePage = () => {
  const { isAuthenticated } = useAuth()
  const [walks, setWalks] = useState<Walk[]>([])
  const [selectedWalk, setSelectedWalk] = useState<Walk | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchWalks()
  }, [isAuthenticated])

  const fetchWalks = useCallback(async () => {
    try {
      setLoading(true)
      
      // Prepare headers with admin authentication if logged in
      const headers: HeadersInit = {
        'Content-Type': 'application/json'
      }
      
      if (isAuthenticated) {
        const adminToken = localStorage.getItem('adminToken')
        if (adminToken) {
          headers['Authorization'] = `Bearer ${adminToken}`
        }
      }
      
      const response = await fetch('/api/walks', {
        method: 'GET',
        headers
      })
      
      if (!response.ok) {
        throw new Error(`Failed to fetch walks: ${response.statusText}`)
      }
      
      const data = await response.json()
      
      // Filter walks based on admin status and sort by latest first
      const filteredWalks = isAuthenticated 
        ? data.sort((a: Walk, b: Walk) => new Date(b.created_date).getTime() - new Date(a.created_date).getTime())
        : data.filter((walk: Walk) => walk.published)
            .sort((a: Walk, b: Walk) => new Date(b.created_date).getTime() - new Date(a.created_date).getTime())
      
      setWalks(filteredWalks)
      
      // Default to latest walk
      if (filteredWalks.length > 0) {
        setSelectedWalk(filteredWalks[0])
      }
    } catch (err) {
      console.error('Error fetching walks:', err)
      setError(err instanceof Error ? err.message : 'Failed to load walks')
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated])

  const handleWalkChange = (walkId: string) => {
    const walk = walks.find(w => w.id === walkId)
    if (walk) {
      setSelectedWalk(walk)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading walks...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center p-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">DayByDay</h1>
          <p className="text-red-600 mb-4">Error: {error}</p>
          <button 
            onClick={fetchWalks}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  if (walks.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center p-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">DayByDay</h1>
          <p className="text-gray-600 mb-4">
            {isAuthenticated ? 'No walks available yet.' : 'No published walks available yet.'}
          </p>
          <a 
            href="/admin" 
            className="text-blue-600 hover:text-blue-800 underline"
          >
            Admin Panel
          </a>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header with walk selector */}
      <header className="bg-white shadow-sm border-b border-gray-200 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-2 sm:space-x-4 min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex-shrink-0">DayByDay</h1>
              {isAuthenticated && (
                <>
                  <span className="text-gray-500 hidden sm:inline">|</span>
                  <a 
                    href="/admin"
                    className="bg-blue-100 text-blue-800 px-2 py-1 rounded-md text-xs sm:text-sm font-medium hover:bg-blue-200 transition-colors flex-shrink-0"
                  >
                    Admin
                  </a>
                </>
              )}
            </div>
            
            <div className="flex items-center space-x-2 sm:space-x-4 min-w-0 flex-1 justify-end">
              {walks.length > 1 ? (
                <div className="flex items-center space-x-1 sm:space-x-2 min-w-0 max-w-[200px] sm:max-w-none">
                  <label htmlFor="walk-select" className="text-xs sm:text-sm font-medium text-gray-700 flex-shrink-0">
                    Walk:
                  </label>
                  <select
                    id="walk-select"
                    value={selectedWalk?.id || ''}
                    onChange={(e) => handleWalkChange(e.target.value)}
                    className="border border-gray-300 rounded-md px-2 sm:px-3 py-1 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-0 max-w-[140px] sm:max-w-[200px] truncate"
                  >
                    {walks.map(walk => (
                      <option key={walk.id} value={walk.id}>
                        {walk.name}{!walk.published ? ' (Unpublished)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              ) : walks.length === 1 && (
                <div className="flex items-center space-x-1 sm:space-x-2">
                  <span className="text-xs sm:text-sm font-medium text-gray-700">Walk:</span>
                  <span className="text-xs sm:text-sm text-gray-900 font-medium">
                    {selectedWalk?.name}
                    {selectedWalk && !selectedWalk.published && (
                      <span className="text-gray-500 ml-1">(Unpublished)</span>
                    )}
                  </span>
                </div>
              )}
              
            </div>
          </div>
        </div>
      </header>

      {/* Main map container - full window minus header */}
      <main className="flex-1 relative">
        {selectedWalk ? (
          <MapComponent walkId={selectedWalk.id} />
        ) : (
          <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
            <div className="text-center p-8">
              <p className="text-gray-600">Select a walk to view details</p>
            </div>
          </div>
        )}
      </main>
    </div>
  )
}

export default HomePage